// src/components/MaterialModal.js
// ============================================================================
// MaterialModal DINÁMICO (Slice 4) — config-EN-DATOS desde las plantillas.
//
// El form se DIBUJA desde `materialTemplates` (elige categoría → pinta los atributos
// de esa familia: enums = selects, números como caliper, labels vía t(labelKey)).
// `displayName`/`specCode`/`code` son READ-ONLY: solo un PREVIEW de lo que generará el
// servidor (la CF `upsertMaterial` los regenera; el cliente nunca los manda).
// Thickness se captura en la unidad NATIVA por familia (rigid=fracción/pulgada ·
// styrene=mil · board/coroplast/wood=mm); la CF lo canonicaliza a mm.
// El guardado va por `onSave` → CF `upsertMaterial`; si la CF responde "duplicado",
// el modal ofrece "sumar stock al existente" (no duplica).
// ============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { listCategories, getTemplate, validateMaterialAttributes, offEnumAttributes, PRESENTATIONS } from '../lib/materialTemplates.mjs';
import { generateDisplayName, generateSpecCode, canonicalThicknessMm } from '../lib/materialNaming.mjs';
import { t } from '../lib/i18n';

// Unidad NATIVA de captura del espesor por familia (la CF la canonicaliza a mm).
const nativeThicknessUnit = (category, attributes) => {
    if (category === 'RIGID' && attributes?.subtype === 'STYRENE') return 'mil';
    const tpl = getTemplate(category);
    return tpl?.thicknessDisplay === 'fraction' ? 'in' : 'mm';
};
// Storage (mm) → valor en la unidad nativa, para precargar el input al editar.
const mmToNative = (mm, unit) => {
    const v = Number(mm);
    if (mm == null || mm === '' || !Number.isFinite(v)) return '';
    if (unit === 'in') return Math.round((v / 25.4) * 10000) / 10000;   // ej. 0.125
    if (unit === 'mil') return Math.round((v / 25.4) * 1000);           // ej. 30
    return Math.round(v * 1000) / 1000;                                 // mm
};

const MaterialModal = ({ isOpen, onClose, onSave, materialData, onAddStockToExisting }) => {
    const categories = listCategories();
    const isEdit = !!materialData?.id;

    const [category, setCategory] = useState(categories[0]);
    const [attributes, setAttributes] = useState({});
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState('');
    const [thicknessNative, setThicknessNative] = useState('');
    const [presentation, setPresentation] = useState('SHEET');
    const [brandCommon, setBrandCommon] = useState('');   // marca cuando NO es atributo de la familia
    const [supplier, setSupplier] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [dup, setDup] = useState(null);                 // material existente (respuesta de dedup)

    const tpl = getTemplate(category);
    const brandIsAttribute = !!tpl?.attributes?.some((a) => a.field === 'brand');
    const nativeUnit = nativeThicknessUnit(category, attributes);
    // #2/#4 (patch): el espesor NO se ingresa en corrugado (derivado de la fluta) ni en
    // paperboard (el caliper en PT es el espesor) → se oculta el input.
    const thicknessHidden = category === 'CORRUGATED' || category === 'PAPERBOARD';

    useEffect(() => {
        const initCat = (materialData?.category && getTemplate(materialData.category)) ? materialData.category : categories[0];
        const initAttrs = materialData?.attributes ? { ...materialData.attributes } : {};
        setCategory(initCat);
        setAttributes(initAttrs);
        setWidth(materialData?.dimensions?.width ?? '');
        setHeight(materialData?.dimensions?.height ?? '');
        setThicknessNative(mmToNative(materialData?.thickness?.value, nativeThicknessUnit(initCat, initAttrs)));
        setPresentation(PRESENTATIONS.includes(materialData?.presentation) ? materialData.presentation : 'SHEET');
        setBrandCommon(getTemplate(initCat)?.attributes?.some((a) => a.field === 'brand') ? '' : (materialData?.brand || ''));
        setSupplier(materialData?.supplier || '');
        setImageUrl(materialData?.imageUrl || '');
        setImagePreview(materialData?.imageUrl || '');
        setImageFile(null);
        setError(''); setDup(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [materialData, isOpen]);

    // Cambiar de familia resetea los atributos (cada familia tiene su ficha).
    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setAttributes({});
        setThicknessNative('');
        setBrandCommon('');
    };
    const setAttr = (field, value) => setAttributes((prev) => ({ ...prev, [field]: value }));

    const setImageFromFile = (file) => {
        if (!file) return;
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };
    const handleImageChange = (e) => { if (e.target.files && e.target.files[0]) setImageFromFile(e.target.files[0]); };
    const handlePaste = (e) => {
        const items = e.clipboardData?.items || [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const file = items[i].getAsFile();
                if (file) { setImageFromFile(file); e.preventDefault(); }
                break;
            }
        }
    };

    // PREVIEW read-only (lo que generará el servidor) + validación en vivo.
    const preview = useMemo(() => {
        const mm = thicknessNative === '' ? null : canonicalThicknessMm({ value: Number(thicknessNative), unit: nativeUnit });
        const dimsOk = Number(width) > 0 && Number(height) > 0;
        const common = {
            dimensions: dimsOk ? { width: Number(width), height: Number(height), unit: 'in' } : null,
            thickness: mm == null ? null : { value: mm, unit: 'mm' },
        };
        const errors = validateMaterialAttributes(category, { attributes, thickness: common.thickness, presentation });
        return {
            mm,
            dimsOk,
            errors,
            displayName: dimsOk ? generateDisplayName(category, attributes, common) : '—',
            specCode: dimsOk ? generateSpecCode(category, attributes, common) : '—',
        };
    }, [category, attributes, width, height, thicknessNative, nativeUnit, presentation]);

    const canSave = preview.dimsOk && preview.errors.length === 0 && !busy;

    const buildPayload = () => ({
        ...(isEdit ? { id: materialData.id } : {}),
        category,
        attributes,
        dimensions: { width: Number(width), height: Number(height), unit: 'in' },
        thickness: thicknessNative === '' ? null : { value: Number(thicknessNative), unit: nativeUnit },
        presentation,
        ...(brandIsAttribute ? {} : { brand: brandCommon || null }),
        supplier: supplier || null,
        imageUrl,
    });

    const handleSave = async () => {
        setError(''); setBusy(true);
        try {
            const res = await onSave(buildPayload(), imageFile);
            if (res?.duplicate) { setDup(res.existing || null); return; }
            onClose();
        } catch (e) {
            console.error('upsertMaterial falló:', e);
            setError(e.message || 'No se pudo guardar el material.');
        } finally {
            setBusy(false);
        }
    };

    if (!isOpen) return null;

    const fieldLabel = (def) => `${t(def.labelKey)}${def.required ? ' *' : ''}`;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onPaste={handlePaste}>
                <h2 className="text-2xl font-bold mb-6">{isEdit ? t('matModal.editTitle') : t('matModal.newTitle')}</h2>

                {/* Banner de duplicado: ofrece sumar stock al existente (no duplica). */}
                {dup && (
                    <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-sm space-y-2">
                        <p className="font-semibold">{t('matModal.dupTitle')}</p>
                        <p>{dup.displayName || dup.specCode || dup.code} · {dup.code || ''}{dup.stock ? ` · ${t('matModal.dupStock')}: ${dup.stock.quantity}` : ''}</p>
                        <div className="flex gap-2">
                            <button onClick={() => { onAddStockToExisting && onAddStockToExisting(dup); }}
                                className="px-3 py-1.5 text-sm font-bold text-white bg-amber-600 rounded-lg hover:bg-amber-700">
                                {t('matModal.dupAddStock')}
                            </button>
                            <button onClick={() => setDup(null)}
                                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">
                                {t('matModal.dupKeepEditing')}
                            </button>
                        </div>
                    </div>
                )}

                <div className="space-y-4">
                    {/* Categoría + presentación */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('matModal.category')}</label>
                            <select value={category} onChange={handleCategoryChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                                {categories.map((c) => <option key={c} value={c}>{t(`matCat.${c}`)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('matAttr.presentation')}</label>
                            <select value={presentation} onChange={(e) => setPresentation(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                                {PRESENTATIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Atributos de la familia (dibujados desde la plantilla) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(tpl?.attributes || []).map((def) => (
                            <div key={def.field}>
                                <label className="block text-sm font-medium text-gray-700">{fieldLabel(def)}</label>
                                {def.type === 'enum' ? (
                                    <select value={attributes[def.field] || ''} onChange={(e) => setAttr(def.field, e.target.value)}
                                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                                        <option value="">—</option>
                                        {/* #6 (patch): valor guardado fuera del catálogo → se MUESTRA (no se coacciona) y
                                            obliga a elegir uno válido (la validación enum bloquea el guardado). */}
                                        {attributes[def.field] && !def.options.includes(attributes[def.field]) && (
                                            <option value={attributes[def.field]}>{attributes[def.field]} — {t('matModal.offEnum')}</option>
                                        )}
                                        {def.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                ) : (
                                    <>
                                        <input type="number" value={attributes[def.field] ?? ''} onChange={(e) => setAttr(def.field, e.target.value)}
                                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                                        {/* #4 (patch): caliper en PT con pulgada de referencia (nunca mm en UI). */}
                                        {def.field === 'caliper' && attributes.caliper !== undefined && attributes.caliper !== '' && Number.isFinite(Number(attributes.caliper)) && (
                                            <p className="text-xs text-gray-400 mt-1">{`${attributes.caliper} PT (${(Number(attributes.caliper) / 1000).toFixed(3)}")`}</p>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Dimensiones (pulgadas, MVP) + espesor nativo */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            {/* #3 (patch): en corrugado el ANCHO es la dirección de fluta (convenio w = fluta,
                                consistente con la regla m1 de retales). */}
                            <label className="block text-sm font-medium text-gray-700">{(category === 'CORRUGATED' ? t('matModal.widthFlute') : t('matModal.width'))} (in) *</label>
                            <input type="number" value={width} onChange={(e) => setWidth(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('matModal.height')} (in) *</label>
                            <input type="number" value={height} onChange={(e) => setHeight(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        {/* #2/#4 (patch): sin input de espesor en corrugado (derivado de la fluta) ni
                            paperboard (el caliper en PT es el espesor). */}
                        {!thicknessHidden && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('matAttr.thickness')} ({nativeUnit})</label>
                                <input type="number" value={thicknessNative} onChange={(e) => setThicknessNative(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                            </div>
                        )}
                    </div>

                    {/* Marca común (solo cuando NO es atributo de la familia) + proveedor */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {!brandIsAttribute && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('matAttr.brand')}</label>
                                <input type="text" value={brandCommon} onChange={(e) => setBrandCommon(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('matAttr.supplier')}</label>
                            <input type="text" value={supplier} onChange={(e) => setSupplier(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>

                    {/* PREVIEW read-only de lo que generará el servidor */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm space-y-1">
                        <p className="text-xs font-semibold text-gray-500 uppercase">{t('matModal.previewTitle')}</p>
                        <p><span className="text-gray-500">{t('matModal.code')}:</span> <span className="font-mono">{isEdit ? (materialData.code || '—') : t('matModal.codeAuto')}</span></p>
                        <p><span className="text-gray-500">{t('matModal.displayName')}:</span> <span className="font-medium">{preview.displayName}</span></p>
                        <p><span className="text-gray-500">{t('matModal.specCode')}:</span> <span className="font-mono">{preview.specCode}</span></p>
                        {preview.errors.length > 0 && (
                            <ul className="text-xs text-red-600 list-disc list-inside pt-1">
                                {preview.errors.map((er, i) => <li key={i}>{t(`matAttr.${er.field}`) !== `matAttr.${er.field}` ? `${t(`matAttr.${er.field}`)}: ` : ''}{t(`matValid.${er.rule}`)}</li>)}
                            </ul>
                        )}
                    </div>

                    {/* Imagen */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('matAttr.imageUrl')}</label>
                        <div className="mt-1 flex items-center gap-4">
                            <img src={imagePreview || 'https://placehold.co/100x100/eee/ccc?text=N/A'} className="w-24 h-24 object-cover rounded-md border" alt="" />
                            <div className="w-full">
                                <input type="file" accept="image/*" onChange={handleImageChange} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                <p className="text-xs text-gray-400 mt-1">{t('matModal.imageHint')}</p>
                            </div>
                        </div>
                    </div>

                    {error && <p className="text-sm text-red-600">{error}</p>}
                </div>

                <div className="flex justify-end gap-4 mt-6">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">{t('matModal.cancel')}</button>
                    <button onClick={handleSave} disabled={!canSave}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-40">
                        {busy ? t('matModal.saving') : t('matModal.save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MaterialModal;
